import { CELL } from '../core/config';
import { type Cell, type CellBounds, key, MATERIALS } from '../world/city';
export interface Vec {
  x: number;
  y: number;
  z: number;
}
const directions = [
  [1, 0, 0],
  [-1, 0, 0],
  [0, 1, 0],
  [0, -1, 0],
  [0, 0, 1],
  [0, 0, -1],
];
export function components(cells: ReadonlyMap<number, Cell>): Cell[][] {
  const index = new Map<number, Cell>();
  for (const c of cells.values()) index.set(key(c.x, c.y, c.z), c);
  const result: Cell[][] = [];
  const seen = new Set<number>();
  for (const c of cells.values()) {
    if (seen.has(c.id)) continue;
    const group = [c];
    seen.add(c.id);
    for (let i = 0; i < group.length; i++)
      for (const [dx, dy, dz] of directions) {
        const p = group[i];
        const n = index.get(key(p.x + dx, p.y + dy, p.z + dz));
        if (n && !seen.has(n.id)) {
          seen.add(n.id);
          group.push(n);
        }
      }
    result.push(group);
  }
  return result;
}
export function unsupported(cells: ReadonlyMap<number, Cell>): Cell[][] {
  return components(cells).filter((group) => !group.some(isRoot));
}
export const isRoot = (c: Cell): boolean => c.root ?? c.y === 0;
export interface Damage {
  removed: Cell[];
  weakened: number;
}
const FULL_BOUNDS = { x: 0, y: 0, z: 0, w: 1, h: 1, d: 1 };
function boxDistance(c: Cell, b: CellBounds, point: Vec): number {
  const dx = Math.max(0, (c.x + b.x) * CELL - point.x, point.x - (c.x + b.x + b.w) * CELL),
    dy = Math.max(0, (c.y + b.y) * CELL - point.y, point.y - (c.y + b.y + b.h) * CELL),
    dz = Math.max(0, (c.z + b.z) * CELL - point.z, point.z - (c.z + b.z + b.d) * CELL);
  return Math.hypot(dx, dy, dz);
}
export function cellDistance(c: Cell, point: Vec): number {
  if (!c.parts) return boxDistance(c, c.bounds ?? FULL_BOUNDS, point);
  let best = Infinity;
  for (const b of c.parts) best = Math.min(best, boxDistance(c, b, point));
  return best;
}
/** Distance to the actual occupied cube, so a surface hit never misses its cell. */
export function damage(
  cells: Map<number, Cell>,
  point: Vec,
  radius: number,
  energy: number,
  beforeChange?: (cell: Cell) => void,
): Damage {
  const removed: Cell[] = [];
  let weakened = 0;
  for (const c of cells.values()) {
    const dist = cellDistance(c, point);
    if (dist > radius) continue;
    beforeChange?.(c);
    c.hp -= energy * (0.3 + 0.7 * (1 - dist / Math.max(0.01, radius)));
    if (c.hp <= 0) {
      cells.delete(c.id);
      removed.push(c);
    } else weakened++;
  }
  return { removed, weakened };
}
export interface Box {
  x: number;
  y: number;
  z: number;
  w: number;
  h: number;
  d: number;
}
/** Exact occupancy, not a convex hull: holes remain holes after every edit. */
export function greedyBoxes(cells: Iterable<Cell>, axes = { x: true, y: true, z: true }): Box[] {
  const work = greedyBoxesSteps(cells, axes, Infinity);
  let result = work.next();
  while (!result.done) result = work.next();
  return result.value;
}
export function* greedyBoxesSteps(
  cells: Iterable<Cell>,
  axes = { x: true, y: true, z: true },
  chunk = 1024,
): Generator<void, Box[]> {
  const filled = new Map<number, Cell>();
  // Flood-filled fracture islands do not retain the generator's scan order.
  // Start at each occupied minimum: positive-axis growth from arbitrary cells
  // can turn a solid section into one collider per cell without changing it.
  const ordered: Cell[] = [];
  let work = 0;
  for (const c of cells) {
    ordered.push(c);
    if (++work >= chunk) {
      work = 0;
      yield;
    }
  }
  ordered.sort((a, b) => a.y - b.y || a.z - b.z || a.x - b.x);
  yield;
  for (const c of ordered) {
    filled.set(key(c.x, c.y, c.z), c);
    if (++work >= chunk) {
      work = 0;
      yield;
    }
  }
  const boxes: Box[] = [];
  const has = (x: number, y: number, z: number) => filled.has(key(x, y, z));
  for (const c of filled.values()) {
    const { x, y, z } = c;
    let w = 1,
      d = 1,
      h = 1;
    while (axes.x && has(x + w, y, z)) w++;
    const row = (zz: number, yy: number) => {
      for (let xx = x; xx < x + w; xx++) if (!has(xx, yy, zz)) return false;
      return true;
    };
    while (axes.z && row(z + d, y)) d++;
    const layer = (yy: number) => {
      for (let zz = z; zz < z + d; zz++) if (!row(zz, yy)) return false;
      return true;
    };
    while (axes.y && layer(y + h)) {
      h++;
      work += w * d;
      if (work >= chunk) {
        work = 0;
        yield;
      }
    }
    for (let yy = y; yy < y + h; yy++)
      for (let zz = z; zz < z + d; zz++)
        for (let xx = x; xx < x + w; xx++) {
          filled.delete(key(xx, yy, zz));
          if (++work >= chunk) {
            work = 0;
            yield;
          }
        }
    boxes.push({ x, y, z, w, h, d });
  }
  return boxes;
}
/** Exact cuboid merging also applies to thin panels, slabs and repeated beams.
 * An axis is merged only when the occupied interval fills that cell axis, so
 * gaps between narrow columns or separated shelves can never be filled in.
 */
export function occupiedBoxes(cells: Iterable<Cell>): Box[] {
  const work = occupiedBoxesSteps(cells, Infinity);
  let result = work.next();
  while (!result.done) result = work.next();
  return result.value;
}
export function* occupiedBoxesSteps(cells: Iterable<Cell>, chunk = 1024): Generator<void, Box[]> {
  const groups = new Map<string, { bounds: NonNullable<Cell['bounds']>; cells: Cell[] }>();
  let work = 0;
  for (const cell of cells) {
    for (const b of cell.parts ?? [cell.bounds ?? { x: 0, y: 0, z: 0, w: 1, h: 1, d: 1 }]) {
      const k = `${b.x},${b.y},${b.z},${b.w},${b.h},${b.d}`;
      let group = groups.get(k);
      if (!group) groups.set(k, (group = { bounds: b, cells: [] }));
      group.cells.push(cell);
    }
    if (++work >= chunk) {
      work = 0;
      yield;
    }
  }
  const boxes: Box[] = [];
  for (const { bounds: b, cells: group } of groups.values()) {
    const merged = yield* greedyBoxesSteps(group, { x: b.w === 1, y: b.h === 1, z: b.d === 1 }, chunk);
    for (const box of merged) {
      boxes.push({
        x: box.x + b.x,
        y: box.y + b.y,
        z: box.z + b.z,
        w: box.w - 1 + b.w,
        h: box.h - 1 + b.h,
        d: box.d - 1 + b.d,
      });
      if (++work >= chunk) {
        work = 0;
        yield;
      }
    }
  }
  return boxes.sort((a, b) => a.y - b.y || a.z - b.z || a.x - b.x || a.w - b.w || a.h - b.h || a.d - b.d);
}
/** Spatial sections, then connectivity. No tiny-fragment flood for a large blast. */
export function partition(cells: Cell[], max: number): Cell[][] {
  if (cells.length <= max) return [cells];
  const size = Math.max(3, Math.ceil(Math.cbrt(max * 1.8)));
  const buckets = new Map<string, Map<number, Cell>>();
  for (const c of cells) {
    const k = `${Math.floor(c.x / size)},${Math.floor(c.y / size)},${Math.floor(c.z / size)}`;
    if (!buckets.has(k)) buckets.set(k, new Map());
    buckets.get(k)!.set(c.id, c);
  }
  return [...buckets.values()].flatMap(components);
}
export function mass(cells: Iterable<Cell>): number {
  let m = 0;
  for (const c of cells) m += cellMass(c);
  return m;
}

/** Relative tonnes for game calibration, with panel/beam volume fractions. */
export function cellMass(c: Cell): number {
  return MATERIALS[c.material].density * CELL ** 3 * cellVolume(c);
}
export function cellVolume(c: Cell): number {
  if (c.parts) return c.parts.reduce((n, p) => n + p.w * p.h * p.d, 0);
  return c.bounds
    ? c.bounds.w * c.bounds.h * c.bounds.d
    : c.role === 'glass'
      ? 0.045
      : c.role === 'facade'
        ? 0.12
        : c.role === 'column'
          ? 0.42
          : c.role === 'beam'
            ? 0.32
            : c.role === 'slab' || c.role === 'roof'
              ? 0.28
              : 0.5;
}
export function physicalCenter(c: Cell): Vec {
  const b = c.bounds;
  return {
    x: (c.x + (b ? b.x + b.w / 2 : 0.5)) * CELL,
    y: (c.y + (b ? b.y + b.h / 2 : 0.5)) * CELL,
    z: (c.z + (b ? b.z + b.d / 2 : 0.5)) * CELL,
  };
}
const COMPRESSION_BASE: Record<string, number> = {
  concrete: 660,
  brick: 250,
  metal: 1800,
  wood: 170,
  glass: 5,
  foliage: 0.8,
};
export function compressionCapacity(c: Cell): number {
  const role =
    c.role === 'facade'
      ? 0.12
      : c.role === 'glass'
        ? 0.25
        : c.role === 'roof'
          ? 0.6
          : c.role === 'wall'
            ? 0.85
            : 1;
  const integrity = Math.max(0.12, Math.min(1, c.hp / MATERIALS[c.material].strength));
  const heat = Math.max(
    0.18,
    1 -
      Math.max(0, (c.heat ?? 0) - 0.2) * (c.material === 'metal' ? 0.65 : c.material === 'wood' ? 0.9 : 0.3),
  );
  return (c.capacity ?? (COMPRESSION_BASE[c.material] ?? 200) * role) * integrity * heat;
}
export interface StructuralAssessment {
  loads: Map<number, number>;
  overloads: { id: number; ratio: number; mode: string }[];
  supported: Set<number>;
  maxRatio: number;
}
const structurePaths = new WeakMap<
  ReadonlyMap<number, Cell>,
  {
    version: number;
    members: Cell[];
    neighbors: Int32Array;
    distances: Float64Array;
    discovered: number[];
    order: number[];
  }
>();

/**
 * Quasi-static load routing, not an engineering stress solver. Strong members
 * receive cheaper load paths. Loads and their first moments flow down a rooted
 * graph; finite compression, connection shear and eccentric bending limit each
 * member. Only damaged/loaded clusters are reevaluated by the simulation.
 */
export function evaluateStructure(
  cells: ReadonlyMap<number, Cell>,
  options: { gravity?: number } = {},
): StructuralAssessment {
  const work = evaluateStructureSteps(cells, options);
  let result = work.next();
  while (!result.done) result = work.next();
  return result.value;
}

/** The same load calculation, yielding between bounded groups of members. The
 * caller must discard a result if its cell topology changed during the work. */
export function* evaluateStructureSteps(
  cells: ReadonlyMap<number, Cell>,
  options: { gravity?: number; topologyVersion?: number } = {},
): Generator<void, StructuralAssessment> {
  let visited = 0;
  const cached = options.topologyVersion === undefined ? undefined : structurePaths.get(cells);
  const reusable = cached?.version === options.topologyVersion && cached !== undefined;
  const members = reusable ? cached.members : [...cells.values()];
  const neighbors = reusable ? cached.neighbors : new Int32Array(members.length * 6).fill(-1);
  const distances = reusable ? cached.distances : new Float64Array(members.length).fill(Infinity);
  const discovered = reusable ? cached.discovered : [];
  const order: number[] = reusable ? cached.order : [];
  if (!reusable) {
    const index = new Map<number, number>();
    for (let i = 0; i < members.length; i++) {
      const c = members[i];
      index.set(key(c.x, c.y, c.z), i);
      if (++visited % 256 === 0) yield;
    }
    // Adjacency changes with topology, not temperature or HP. Use compact
    // member indices for the repeated load passes; authoritative IDs remain
    // on the cells and in the published result.
    for (let i = 0; i < members.length; i++) {
      const c = members[i];
      for (let d = 0; d < 6; d++) {
        const [dx, dy, dz] = directions[d];
        neighbors[i * 6 + d] = index.get(key(c.x + dx, c.y + dy, c.z + dz)) ?? -1;
      }
      if (++visited % 256 === 0) yield;
    }
    const heap: { cell: number; cost: number }[] = [];
    const push = (cell: number, cost: number) => {
      let i = heap.length;
      heap.push({ cell, cost });
      while (i > 0) {
        const p = (i - 1) >> 1;
        if (heap[p].cost <= cost) break;
        heap[i] = heap[p];
        i = p;
      }
      heap[i] = { cell, cost };
    };
    const pop = () => {
      const first = heap[0],
        last = heap.pop()!;
      if (heap.length) {
        let i = 0;
        while (i * 2 + 1 < heap.length) {
          let n = i * 2 + 1;
          if (n + 1 < heap.length && heap[n + 1].cost < heap[n].cost) n++;
          if (last.cost <= heap[n].cost) break;
          heap[i] = heap[n];
          i = n;
        }
        heap[i] = last;
      }
      return first;
    };
    for (let i = 0; i < members.length; i++) {
      if (isRoot(members[i])) {
        distances[i] = 0;
        discovered.push(i);
        push(i, 0);
      }
      if (++visited % 256 === 0) yield;
    }
    while (heap.length) {
      if (++visited % 256 === 0) yield;
      const { cell: i, cost } = pop();
      if (distances[i] !== cost) continue;
      const c = members[i];
      order.push(i);
      for (let d = 0; d < 6; d++) {
        const n = neighbors[i * 6 + d],
          dy = directions[d][1];
        if (n < 0) continue;
        const roleCost =
          c.role === 'glass' || c.role === 'facade'
            ? 12
            : c.role === 'column' || c.role === 'beam'
              ? 0.75
              : 1;
        const next = cost + (dy > 0 ? 1 : dy === 0 ? 1.6 : 5) * roleCost;
        if (next < distances[n]) {
          if (distances[n] === Infinity) discovered.push(n);
          distances[n] = next;
          push(n, next);
        }
      }
    }
    if (options.topologyVersion !== undefined)
      structurePaths.set(cells, {
        version: options.topologyVersion,
        members,
        neighbors,
        distances,
        discovered,
        order,
      });
  }
  const loads = new Float64Array(members.length),
    capacities = new Float64Array(members.length),
    momentX = new Float64Array(members.length),
    momentZ = new Float64Array(members.length);
  for (let i = 0; i < members.length; i++) {
    const c = members[i];
    const load = cellMass(c) * (options.gravity ?? 1);
    loads[i] = load;
    capacities[i] = compressionCapacity(c);
    momentX[i] = load * c.x;
    momentZ[i] = load * c.z;
    if (++visited % 256 === 0) yield;
  }
  const overloaded = new Map<number, { id: number; ratio: number; mode: string }>();
  let maxRatio = 0;
  const consider = (c: Cell, ratio: number, mode: string) => {
    maxRatio = Math.max(maxRatio, ratio);
    if (ratio > 1 && ratio > (overloaded.get(c.id)?.ratio ?? 0))
      overloaded.set(c.id, { id: c.id, ratio, mode });
  };
  const parentIndices = new Int32Array(6),
    parentWeights = new Float64Array(6),
    parentDirections = new Int8Array(6);
  for (let i = order.length - 1; i >= 0; i--) {
    if (++visited % 256 === 0) yield;
    const member = order[i],
      c = members[member],
      load = loads[member],
      mx = momentX[member],
      mz = momentZ[member];
    const eccentricity = Math.hypot(mx / load - c.x, mz / load - c.z) * CELL;
    const capacity = capacities[member];
    consider(
      c,
      (load * (1 + eccentricity * 0.14)) / capacity,
      eccentricity > CELL ? 'bending' : 'compression',
    );
    if (isRoot(c)) continue;
    const distance = distances[member];
    let parentCount = 0,
      total = 0;
    for (let d = 0; d < 6; d++) {
      const p = neighbors[member * 6 + d],
        dy = directions[d][1];
      if (p < 0 || distances[p] >= distance - 1e-6) continue;
      const weight = capacities[p] * (dy < 0 ? 3 : dy === 0 ? 0.6 : 0.12);
      parentIndices[parentCount] = p;
      parentWeights[parentCount] = weight;
      parentDirections[parentCount++] = dy;
      total += weight;
    }
    for (let p = 0; p < parentCount; p++) {
      const parent = parentIndices[p],
        shear = parentDirections[p] === 0,
        tension = parentDirections[p] > 0;
      const fraction = parentWeights[p] / Math.max(0.001, total),
        transmitted = load * fraction;
      const shearFactor = shear
        ? c.role === 'beam' || c.role === 'slab' || c.role === 'roof'
          ? 0.2
          : 0.1
        : tension
          ? 0.055
          : 1;
      const edgeCapacity = Math.min(capacity, capacities[parent]) * shearFactor;
      consider(
        c,
        (transmitted * (shear ? 1 + eccentricity * 0.22 : 1)) / edgeCapacity,
        shear ? 'shear/bending' : tension ? 'tension' : 'compression',
      );
      loads[parent] += transmitted;
      momentX[parent] += mx * fraction;
      momentZ[parent] += mz * fraction;
    }
  }
  const publishedLoads = new Map<number, number>(),
    supported = new Set<number>();
  for (let i = 0; i < members.length; i++) {
    publishedLoads.set(members[i].id, loads[i]);
    if (++visited % 256 === 0) yield;
  }
  for (const member of discovered) {
    supported.add(members[member].id);
    if (++visited % 256 === 0) yield;
  }
  return {
    loads: publishedLoads,
    overloads: [...overloaded.values()].sort((a, b) => b.ratio - a.ratio),
    supported,
    maxRatio,
  };
}
